/**
 * Marketing Center — Broadcast Center (KR admins only).
 *
 * Compose a message to Agents / Admins / Agencies and send it over Email,
 * SMS and/or a Voicemail blast (AI OpenAI-TTS voice). Preview the voice,
 * send a test to yourself, then send now or schedule for later.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatPhone } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Mail, MessageSquare, Phone, Send, Users, Loader2, Play, Volume2,
  CalendarClock, Plus, Trash2, CheckCircle2, Megaphone, RefreshCw,
  FileText, Save, ChevronDown, ChevronUp, Voicemail, PhoneOff, Paperclip, X, BarChart3, RotateCcw,
} from 'lucide-react';
import { broadcastAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import RecipientPicker from '@/components/marketing/RecipientPicker';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const CHANNELS = [
  { key: 'email', label: 'Email', icon: Mail, color: 'text-sky-600' },
  { key: 'sms', label: 'SMS', icon: MessageSquare, color: 'text-violet-600' },
  { key: 'voicemail', label: 'Voicemail (AI voice)', icon: Phone, color: 'text-emerald-600' },
];
const AUDIENCES = [
  { key: 'agents', label: 'Agents' },
  { key: 'admins', label: 'Admins' },
  { key: 'agencies', label: 'Agencies' },
];

const STATUS_STYLE = {
  sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sending: 'bg-amber-50 text-amber-700 border-amber-200',
  scheduled: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  queued: 'bg-slate-100 text-slate-600 border-slate-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
};

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

export default function BroadcastCenterTab() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ agents: 0, admins: 0, agencies: 0, voices: [] });
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});

  const [channels, setChannels] = useState(new Set(['email']));
  const [audiences, setAudiences] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [manual, setManual] = useState([]);
  const [pickedAgents, setPickedAgents] = useState([]);
  const [downlineAgencies, setDownlineAgencies] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachBusy, setAttachBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailIsHtml, setEmailIsHtml] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [voiceText, setVoiceText] = useState('');
  const [voice, setVoice] = useState('alloy');
  const [voiceLang, setVoiceLang] = useState('en');
  const [audioSource, setAudioSource] = useState('tts'); // 'tts' | 'upload'
  const [uploadedAudioToken, setUploadedAudioToken] = useState(null);
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [callerNumbers, setCallerNumbers] = useState([]);
  const [fromNumber, setFromNumber] = useState('');
  const [smsFromNumber, setSmsFromNumber] = useState('');
  const [phoneListText, setPhoneListText] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [testPhone, setTestPhone] = useState(user?.phone || '');
  const [busy, setBusy] = useState(false);
  const [ttsBusy, setTtsBusy] = useState(false);
  const audioRef = useRef(null);

  const loadCounts = useCallback(async () => {
    try { const r = await broadcastAPI.audienceCounts(); setCounts(r.data); }
    catch (e) { /* 403 for non-KR */ }
  }, []);
  const loadHistory = useCallback(async () => {
    setLoadingHist(true);
    try { const r = await broadcastAPI.list(); setHistory(r.data.items || []); }
    catch (e) { /* noop */ }
    finally { setLoadingHist(false); }
  }, []);
  const loadTemplates = useCallback(async () => {
    try { const r = await broadcastAPI.listTemplates(); setTemplates(r.data.items || []); }
    catch (e) { /* noop */ }
  }, []);
  const loadCallers = useCallback(async () => {
    try {
      const r = await broadcastAPI.callerNumbers();
      setCallerNumbers(r.data.numbers || []);
      setFromNumber((prev) => prev || r.data.default || (r.data.numbers?.[0]?.number) || '');
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { loadCounts(); loadHistory(); loadTemplates(); loadCallers(); }, [loadCounts, loadHistory, loadTemplates, loadCallers]);

  const parsedPhones = Array.from(new Set(
    phoneListText
      .split(/[\r\n,;\t|]+/)            // one number per line, comma, semicolon, tab or pipe
      .map((s) => s.trim())
      .map((s) => {                      // keep a single leading + and all digits
        const m = s.match(/\+?[\d()\-.\s]{7,}/);
        if (!m) return '';
        const raw = m[0];
        const plus = raw.trim().startsWith('+') ? '+' : '';
        const digits = raw.replace(/\D/g, '');
        return digits.length >= 7 ? plus + digits : '';
      })
      .filter(Boolean),
  ));

  const onAudioUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setAudioUploading(true);
    try {
      const r = await broadcastAPI.uploadAudio(f);
      setUploadedAudioToken(r.data.audio_token);
      setUploadedAudioUrl(`${process.env.REACT_APP_BACKEND_URL}${r.data.audio_url}`);
      toast.success('Audio uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Audio upload failed');
    } finally { setAudioUploading(false); }
  };

  const toggleSet = (setter) => (key) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleChannel = toggleSet(setChannels);
  const toggleAudience = toggleSet(setAudiences);

  const onHtmlUpload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!/\.(html?|htm)$/i.test(f.name) && f.type !== 'text/html') {
      toast.error('Please upload an .html file.');
      return;
    }
    if (f.size > 1024 * 1024) { toast.error('HTML file is too large (max 1 MB).'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setEmailBody(String(reader.result || ''));
      setEmailIsHtml(true);
      toast.success(`Loaded ${f.name}`);
    };
    reader.onerror = () => toast.error('Could not read the file.');
    reader.readAsText(f);
  };

  const refreshPreview = useCallback(async () => {
    try {
      const r = await broadcastAPI.previewRecipients({
        audiences: Array.from(audiences),
        agent_ids: pickedAgents.map((a) => a.id),
        downline_agency_ids: downlineAgencies,
        manual_recipients: [...manual, ...parsedPhones.map((p) => ({ phone: p }))],
      });
      setPreview(r.data);
    } catch (e) { setPreview(null); }
  }, [audiences, pickedAgents, downlineAgencies, manual, parsedPhones]);

  useEffect(() => {
    const id = setTimeout(() => { refreshPreview(); }, 500);
    return () => clearTimeout(id);
  }, [refreshPreview]);

  const playTts = async () => {
    if (!voiceText.trim()) { toast.error('Write the voice message first.'); return; }
    setTtsBusy(true);
    try {
      const r = await broadcastAPI.ttsPreview({ text: voiceText, voice });
      const src = `data:${r.data.mime};base64,${r.data.audio_base64}`;
      if (audioRef.current) { audioRef.current.src = src; audioRef.current.play(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate voice preview');
    } finally { setTtsBusy(false); }
  };

  const basePayload = () => ({
    title: title || 'Untitled broadcast',
    channels: Array.from(channels),
    audiences: Array.from(audiences),
    agent_ids: pickedAgents.map((a) => a.id),
    downline_agency_ids: downlineAgencies,
    manual_recipients: manual.filter((m) => m.email || m.phone),
    phone_list: parsedPhones,
    attachments: attachments.map((a) => ({ token: a.token, filename: a.filename, mime: a.mime })),
    from_number: fromNumber,
    sms_from_number: smsFromNumber,
    email_subject: emailSubject,
    email_body: emailBody,
    email_is_html: emailIsHtml,
    sms_body: smsBody,
    voice_text: voiceText,
    voice,
    voice_language: voiceLang,
    audio_token: audioSource === 'upload' ? uploadedAudioToken : null,
  });

  const sendTest = async () => {
    if (channels.size === 0) { toast.error('Pick at least one channel.'); return; }
    setBusy(true);
    try {
      const r = await broadcastAPI.test({ ...basePayload(), test_email: testEmail, test_phone: testPhone });
      const res = r.data.result || {};
      const lines = Object.entries(res).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`);
      toast.success(`Test sent — ${lines.join(' · ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (channels.size === 0) { toast.error('Pick at least one channel.'); return; }
    if (audiences.size === 0 && manual.filter((m) => m.email || m.phone).length === 0
        && parsedPhones.length === 0 && pickedAgents.length === 0 && downlineAgencies.length === 0) {
      toast.error('Choose an audience, pick agents/agencies, add recipients, or paste a phone list.'); return;
    }
    if (channels.has('voicemail') && audioSource === 'upload' && !uploadedAudioToken) {
      toast.error('Upload an audio file for the voicemail, or switch to AI voice.'); return;
    }
    if (channels.has('voicemail') && audioSource === 'tts' && !voiceText.trim()) {
      toast.error('Write the voice message, or switch to Upload audio.'); return;
    }
    await refreshPreview();
    setConfirmOpen(true);
  };

  const doSend = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const payload = { ...basePayload() };
      if (scheduledAt) payload.scheduled_at = new Date(scheduledAt).toISOString();
      await broadcastAPI.create(payload);
      toast.success(scheduledAt ? 'Broadcast scheduled' : 'Broadcast is sending');
      setTitle(''); setEmailSubject(''); setEmailBody(''); setEmailIsHtml(false); setSmsBody(''); setVoiceText(''); setScheduledAt('');
      setPhoneListText(''); setUploadedAudioToken(null); setUploadedAudioUrl(null); setAudioSource('tts');
      setPickedAgents([]); setDownlineAgencies([]); setAttachments([]);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create broadcast');
    } finally { setBusy(false); }
  };

  const sendNow = async (id) => {
    try { await broadcastAPI.sendNow(id); toast.success('Sending now'); loadHistory(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send'); }
  };
  const removeBroadcast = async (id) => {
    if (!window.confirm('Delete this broadcast?')) return;
    try { await broadcastAPI.remove(id); loadHistory(); }
    catch (e) { toast.error('Could not delete'); }
  };

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.template_id === tid);
    if (!t) return;
    if (t.channels?.length) setChannels(new Set(t.channels));
    setEmailSubject(t.email_subject || '');
    setEmailBody(t.email_body || '');
    setEmailIsHtml(!!t.email_is_html);
    setSmsBody(t.sms_body || '');
    setVoiceText(t.voice_text || '');
    if (t.voice) setVoice(t.voice);
    if (t.voice_language) setVoiceLang(t.voice_language);
    toast.success(`Loaded template “${t.name}”`);
  };
  const saveTemplate = async () => {
    const name = window.prompt('Save this content as a template. Name:');
    if (!name) return;
    try {
      await broadcastAPI.createTemplate({
        name, channels: Array.from(channels),
        email_subject: emailSubject, email_body: emailBody, email_is_html: emailIsHtml,
        sms_body: smsBody, voice_text: voiceText, voice, voice_language: voiceLang,
      });
      toast.success('Template saved');
      loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save template'); }
  };
  const deleteTemplate = async (tid, e) => {
    e?.stopPropagation?.();
    if (!window.confirm('Delete this template?')) return;
    try { await broadcastAPI.deleteTemplate(tid); loadTemplates(); } catch (_) { /* noop */ }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!details[id]) {
      try { const r = await broadcastAPI.get(id); setDetails((p) => ({ ...p, [id]: r.data })); }
      catch (_) { /* noop */ }
    }
  };
  const refreshDetails = async (id) => {
    setDetails((p) => ({ ...p, [id]: undefined }));
    try { const r = await broadcastAPI.get(id); setDetails((p) => ({ ...p, [id]: r.data })); }
    catch (_) { /* noop */ }
  };

  const addManual = () => setManual((p) => [...p, { name: '', email: '', phone: '' }]);
  const updManual = (i, patch) => setManual((p) => p.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const delManual = (i) => setManual((p) => p.filter((_, idx) => idx !== i));

  const onAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setAttachBusy(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is larger than 10 MB`); continue; }
        const r = await broadcastAPI.uploadAttachment(f);
        setAttachments((p) => [...p, r.data]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Attachment upload failed');
    } finally { setAttachBusy(false); }
  };
  const removeAttachment = (token) => setAttachments((p) => p.filter((a) => a.token !== token));

  return (
    <div className="space-y-5" data-testid="broadcast-center-tab">
      <audio ref={audioRef} className="hidden" />
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Composer */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Megaphone size={16} className="text-emerald-600" /> New Broadcast</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Internal title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. April carrier update" data-testid="broadcast-title" />
              </div>

              <div className="flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-[11px] flex items-center gap-1 text-slate-600 mb-1"><FileText size={12} /> Load a saved template</Label>
                  <Select onValueChange={applyTemplate} value="">
                    <SelectTrigger className="h-8 text-xs bg-white" data-testid="broadcast-template-select">
                      <SelectValue placeholder={templates.length ? 'Choose a template…' : 'No templates yet'} />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.template_id} value={t.template_id}>
                          <span className="inline-flex items-center justify-between gap-3 w-full">
                            <span>{t.name}</span>
                            <Trash2 size={12} className="text-rose-400 hover:text-rose-600" onClick={(e) => deleteTemplate(t.template_id, e)} />
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={saveTemplate} data-testid="broadcast-save-template">
                  <Save size={13} className="mr-1" /> Save as template
                </Button>
              </div>

              <div>
                <Label className="text-xs mb-1.5 block">Channels</Label>
                <div className="flex flex-wrap gap-2">
                  {CHANNELS.map((c) => {
                    const Icon = c.icon; const on = channels.has(c.key);
                    return (
                      <button key={c.key} type="button" onClick={() => toggleChannel(c.key)}
                        data-testid={`broadcast-channel-${c.key}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'}`}>
                        <Icon size={13} className={on ? 'text-white' : c.color} /> {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-xs mb-1.5 block">Audience <span className="text-slate-400 font-normal">— quick groups</span></Label>
                <div className="flex flex-wrap gap-2">
                  {AUDIENCES.map((a) => {
                    const on = audiences.has(a.key);
                    return (
                      <button key={a.key} type="button" onClick={() => toggleAudience(a.key)}
                        data-testid={`broadcast-audience-${a.key}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-400'}`}>
                        <Users size={13} /> {a.label} <span className={on ? 'opacity-80' : 'text-slate-400'}>({counts[a.key] ?? 0})</span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3">
                  <Label className="text-xs mb-1.5 block">Or target precisely</Label>
                  <RecipientPicker
                    pickedAgents={pickedAgents}
                    onPickedAgentsChange={setPickedAgents}
                    downlineAgencies={downlineAgencies}
                    onDownlineChange={setDownlineAgencies}
                  />
                </div>

                <div className="mt-2 space-y-1.5">
                  {manual.map((m, i) => (
                    <div key={i} className="flex gap-1.5 items-center" data-testid={`broadcast-manual-${i}`}>
                      <Input value={m.name} onChange={(e) => updManual(i, { name: e.target.value })} placeholder="Name" className="h-8 text-xs" />
                      <Input value={m.email} onChange={(e) => updManual(i, { email: e.target.value })} placeholder="Email" className="h-8 text-xs" />
                      <Input value={m.phone} onChange={(e) => updManual(i, { phone: e.target.value })} placeholder="Phone" className="h-8 text-xs" />
                      <Button variant="ghost" size="sm" onClick={() => delManual(i)}><Trash2 size={13} className="text-rose-500" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addManual} data-testid="broadcast-add-manual"><Plus size={12} className="mr-1" /> Add recipient manually</Button>
                </div>
                <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5">
                  <Label className="text-[11px] flex items-center gap-1 text-slate-600 mb-1">
                    <Phone size={12} /> Phone list for voicemail / SMS (paste or upload)
                  </Label>
                  <Textarea
                    rows={2}
                    value={phoneListText}
                    onChange={(e) => setPhoneListText(e.target.value)}
                    placeholder="Paste phone numbers — one per line or comma-separated"
                    className="text-xs bg-white"
                    data-testid="broadcast-phone-list"
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <label className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md bg-white text-slate-700 hover:bg-slate-100 border border-slate-200 cursor-pointer" data-testid="broadcast-phone-list-upload">
                      <FileText size={12} /> Upload CSV / TXT
                      <input type="file" accept=".csv,.txt,text/csv,text/plain" className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0]; e.target.value = '';
                          if (!f) return;
                          const reader = new FileReader();
                          reader.onload = () => setPhoneListText((prev) => (prev ? prev + '\n' : '') + String(reader.result || ''));
                          reader.readAsText(f);
                        }} />
                    </label>
                    <span className="text-[11px] text-emerald-600 font-medium" data-testid="broadcast-phone-list-count">{parsedPhones.length} number(s)</span>
                  </div>
                </div>
              </div>

              {channels.has('email') && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1 text-sky-700"><Mail size={12} /> Email</Label>
                    <div className="flex items-center gap-1.5">
                      <label className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 cursor-pointer" data-testid="broadcast-email-upload-html">
                        <FileText size={12} /> Upload HTML
                        <input type="file" accept=".html,.htm,text/html" className="hidden" onChange={onHtmlUpload} />
                      </label>
                      {!emailIsHtml ? (
                        <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setEmailIsHtml(true)} data-testid="broadcast-email-paste-html">
                          <FileText size={12} className="mr-1" /> Paste HTML
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => setEmailIsHtml(false)} data-testid="broadcast-email-plain">
                          Switch to plain text
                        </Button>
                      )}
                    </div>
                  </div>
                  <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject" data-testid="broadcast-email-subject" />
                  {emailIsHtml ? (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1 text-sky-700"><FileText size={12} /> HTML mode — paste or edit the code below ({Math.round(emailBody.length / 1024)} KB). Sent exactly as-is.</span>
                      </div>
                      <Textarea
                        rows={6}
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        placeholder="Paste your HTML here…  e.g. <html><body>…</body></html>"
                        className="font-mono text-[11px]"
                        data-testid="broadcast-email-html-source"
                      />
                      <p className="text-[10px] text-slate-400">Live preview:</p>
                      <iframe
                        title="html-email-preview"
                        srcDoc={emailBody}
                        className="w-full h-56 rounded-md border border-slate-200 bg-white"
                        sandbox=""
                        data-testid="broadcast-email-html-preview"
                      />
                    </div>
                  ) : (
                    <Textarea rows={4} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Email body… (or paste / upload HTML)" data-testid="broadcast-email-body" />
                  )}

                  {/* Attachments */}
                  <div className="pt-1" data-testid="broadcast-attachments">
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-[11px] flex items-center gap-1 text-slate-600"><Paperclip size={12} /> Attachments</Label>
                      <label className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-900 cursor-pointer" data-testid="broadcast-attach-btn">
                        {attachBusy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add file
                        <input type="file" multiple className="hidden" onChange={onAttachmentUpload} disabled={attachBusy}
                               data-testid="broadcast-attach-input" />
                      </label>
                    </div>
                    {attachments.length === 0 ? (
                      <p className="text-[10px] text-slate-400">Attach PDFs, images or documents (≤ 10 MB each).</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {attachments.map((a) => (
                          <span key={a.token} className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 border border-slate-200 pl-2 pr-1.5 py-1 text-[11px] text-slate-700"
                                data-testid={`broadcast-attachment-${a.token}`}>
                            <Paperclip size={11} className="text-slate-400" />
                            <span className="max-w-[160px] truncate">{a.filename}</span>
                            <span className="text-slate-400">{a.size ? `${Math.max(1, Math.round(a.size / 1024))} KB` : ''}</span>
                            <button type="button" onClick={() => removeAttachment(a.token)}
                                    className="text-slate-400 hover:text-rose-500" data-testid={`broadcast-attachment-remove-${a.token}`}><X size={12} /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {channels.has('sms') && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <Label className="text-xs flex items-center gap-1 text-violet-700"><MessageSquare size={12} /> SMS</Label>
                  <Textarea rows={3} value={smsBody} onChange={(e) => setSmsBody(e.target.value)} placeholder="Text message… (keep it short)" data-testid="broadcast-sms-body" />
                  <p className="text-[10px] text-slate-400">{smsBody.length} chars</p>
                  <div>
                    <Label className="text-[11px] text-slate-500 mb-1 block">Send from (number)</Label>
                    <Select value={smsFromNumber || '__default__'} onValueChange={(v) => setSmsFromNumber(v === '__default__' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid="broadcast-sms-from-number"><SelectValue placeholder="Default number" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">Default number</SelectItem>
                        {callerNumbers.map((n) => <SelectItem key={n.number} value={n.number}>{n.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-400 mt-1">
                      The number the text message is sent from. Names come from Settings → Calling → Phone numbers. Leave as default to use the account SMS number.
                    </p>
                  </div>
                </div>
              )}
              {channels.has('voicemail') && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Label className="text-xs flex items-center gap-1 text-emerald-700"><Phone size={12} /> Voicemail blast</Label>
                    <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="broadcast-audio-source">
                      {[['tts', 'AI voice'], ['upload', 'Upload audio']].map(([v, lbl]) => (
                        <button key={v} type="button" onClick={() => setAudioSource(v)}
                          data-testid={`broadcast-audio-source-${v}`}
                          className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${audioSource === v ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-[11px] text-slate-500 mb-1 block">Call from (caller ID)</Label>
                    <Select value={fromNumber || '__default__'} onValueChange={(v) => setFromNumber(v === '__default__' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid="broadcast-from-number"><SelectValue placeholder="Default number" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">Default number</SelectItem>
                        {callerNumbers.map((n) => <SelectItem key={n.number} value={n.number}>{n.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-400 mt-1">
                      An agent can appear as their own number here only if it's an account number or a <strong>verified caller ID</strong> in Twilio (a personal cell can't be used as caller ID until verified).
                    </p>
                  </div>

                  {audioSource === 'upload' ? (
                    <div className="space-y-2">
                      <label className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 cursor-pointer" data-testid="broadcast-audio-upload">
                        {audioUploading ? <Loader2 size={12} className="animate-spin" /> : <Volume2 size={12} />}
                        {uploadedAudioToken ? 'Replace audio' : 'Upload MP3 / WAV'}
                        <input type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg,.mp3,.wav" className="hidden" onChange={onAudioUpload} />
                      </label>
                      {uploadedAudioUrl && (
                        <audio controls src={uploadedAudioUrl} className="w-full h-9" data-testid="broadcast-audio-player" />
                      )}
                      <p className="text-[10px] text-slate-400">
                        Accepted formats: <strong>MP3</strong> or <strong>WAV</strong> (also OGG), up to 15 MB. For best quality on phone calls, use a clear voice recording — mono, 8&nbsp;kHz or higher. The clip plays when the call is answered or a voicemail is reached.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <Label className="text-[11px] text-slate-500">AI voice message</Label>
                        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="broadcast-voice-lang">
                          {[['en', 'English'], ['es', 'Español']].map(([v, lbl]) => (
                            <button key={v} type="button" onClick={() => setVoiceLang(v)}
                              data-testid={`broadcast-voice-lang-${v}`}
                              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${voiceLang === v ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                      <Textarea rows={3} value={voiceText} onChange={(e) => setVoiceText(e.target.value)}
                        placeholder={voiceLang === 'es'
                          ? 'Escribe en español lo que la voz debe decir…'
                          : 'What the AI voice should say when answered or a voicemail is reached…'}
                        data-testid="broadcast-voice-text" />
                      <div className="flex items-center gap-2">
                        <Select value={voice} onValueChange={setVoice}>
                          <SelectTrigger className="w-36 h-8 text-xs" data-testid="broadcast-voice-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(counts.voices || ['alloy']).map((v) => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={playTts} disabled={ttsBusy} data-testid="broadcast-voice-preview">
                          {ttsBusy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Volume2 size={13} className="mr-1" />} Preview voice
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Side: preview + test + send */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Users size={14} /> Recipients</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-semibold" data-testid="broadcast-preview-total">{preview?.total ?? '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">With email</span><span>{preview?.with_email ?? '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">With phone</span><span>{preview?.with_phone ?? '—'}</span></div>
              <Button variant="ghost" size="sm" className="w-full mt-1" onClick={refreshPreview}><RefreshCw size={12} className="mr-1" /> Refresh</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm">Send a test to yourself</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Test email" className="h-8 text-xs" data-testid="broadcast-test-email" />
              <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Test phone" className="h-8 text-xs" data-testid="broadcast-test-phone" />
              <Button variant="outline" size="sm" className="w-full" onClick={sendTest} disabled={busy} data-testid="broadcast-send-test">
                {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Play size={13} className="mr-1" />} Send test
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><CalendarClock size={14} /> Schedule (optional)</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="h-9 text-xs" data-testid="broadcast-schedule-at" />
              <p className="text-[10px] text-slate-400">Leave empty to send immediately. Scheduled sends run on the next 15-minute cycle.</p>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={busy} data-testid="broadcast-submit">
                {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
                {scheduledAt ? 'Schedule broadcast' : 'Send now'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* History */}
      <Card>
        <CardHeader><CardTitle className="text-base">Broadcast history</CardTitle></CardHeader>
        <CardContent>
          {loadingHist ? (
            <div className="py-8 text-center text-slate-400"><Loader2 className="animate-spin inline" size={18} /></div>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="broadcast-history-empty">No broadcasts yet.</p>
          ) : (
            <div className="space-y-2" data-testid="broadcast-history">
              {history.map((b) => {
                const hasVoice = (b.channels || []).includes('voicemail');
                const open = expandedId === b.broadcast_id;
                const det = details[b.broadcast_id];
                return (
                <div key={b.broadcast_id} className="rounded-lg border border-slate-200" data-testid={`broadcast-row-${b.broadcast_id}`}>
                  <div className="flex items-center justify-between gap-3 p-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 truncate">{b.title}</span>
                        <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[b.status] || STATUS_STYLE.queued}`}>{b.status}</Badge>
                        {(b.channels || []).map((c) => <Badge key={c} variant="outline" className="text-[10px] bg-slate-50">{c}</Badge>)}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {b.scheduled_at ? `Scheduled: ${fmt(b.scheduled_at)}` : `Created: ${fmt(b.created_at)}`}
                        {b.stats?.total != null && ` · ${b.stats.total} recipients`}
                        {b.stats?.email_sent ? ` · ✉ ${b.stats.email_sent}` : ''}
                        {b.stats?.sms_sent ? ` · 💬 ${b.stats.sms_sent}` : ''}
                        {b.stats?.calls_placed ? ` · 📞 ${b.stats.calls_placed}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => toggleExpand(b.broadcast_id)} data-testid={`broadcast-results-${b.broadcast_id}`} title="View report">
                        <BarChart3 size={14} className="text-slate-500 mr-1" /> Report
                        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </Button>
                      {b.status === 'scheduled' && (
                        <Button variant="ghost" size="sm" onClick={() => sendNow(b.broadcast_id)} data-testid={`broadcast-sendnow-${b.broadcast_id}`} title="Send now">
                          <Send size={14} className="text-emerald-600" />
                        </Button>
                      )}
                      {b.status === 'sent' && <CheckCircle2 size={16} className="text-emerald-500" />}
                      <Button variant="ghost" size="sm" onClick={() => removeBroadcast(b.broadcast_id)} data-testid={`broadcast-delete-${b.broadcast_id}`}>
                        <Trash2 size={14} className="text-rose-500" />
                      </Button>
                    </div>
                  </div>
                  {open && (
                    <div className="border-t border-slate-100 p-3 bg-slate-50/60" data-testid={`broadcast-results-panel-${b.broadcast_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-slate-700">Delivery report</span>
                        <Button variant="ghost" size="sm" className="h-6 text-[11px]" onClick={() => refreshDetails(b.broadcast_id)}>
                          <RefreshCw size={11} className="mr-1" /> Refresh
                        </Button>
                      </div>
                      {!det ? (
                        <div className="text-center text-slate-400 py-3"><Loader2 size={14} className="animate-spin inline" /></div>
                      ) : (
                        <BroadcastReport det={det} channels={b.channels || []}
                          broadcastId={b.broadcast_id}
                          onRetried={() => { refreshDetails(b.broadcast_id); loadHistory(); }} />
                      )}
                    </div>
                  )}
                </div>
              ); })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="broadcast-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{scheduledAt ? 'Schedule this broadcast?' : 'Send this broadcast now?'}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  This will reach <strong data-testid="broadcast-confirm-count">{preview?.total ?? 0}</strong> recipient(s)
                  {scheduledAt ? <> on <strong>{fmt(new Date(scheduledAt).toISOString())}</strong></> : ' immediately'}.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from(channels).map((c) => (
                    <Badge key={c} variant="outline" className="capitalize bg-slate-50">{c}</Badge>
                  ))}
                </div>
                {(channels.has('sms') || channels.has('voicemail')) && (
                  <p className="text-amber-700 text-xs">
                    Heads up: this will place real {channels.has('voicemail') ? 'phone calls' : ''}{channels.has('sms') && channels.has('voicemail') ? ' and ' : ''}{channels.has('sms') ? 'text messages' : ''} to {preview?.with_phone ?? 0} number(s).
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="broadcast-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSend} className="bg-emerald-600 hover:bg-emerald-700" data-testid="broadcast-confirm-send">
              {scheduledAt ? 'Schedule' : 'Send now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const OUTCOME_STYLE = {
  answered: { label: 'Answered', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: Phone },
  voicemail: { label: 'Voicemail', cls: 'bg-sky-50 text-sky-700 border-sky-200', Icon: Voicemail },
  'no-answer': { label: 'No answer', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: PhoneOff },
  busy: { label: 'Busy', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: PhoneOff },
  failed: { label: 'Failed', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: PhoneOff },
  canceled: { label: 'Canceled', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: PhoneOff },
  pending: { label: 'Ringing…', cls: 'bg-indigo-50 text-indigo-600 border-indigo-200', Icon: Loader2 },
};

function CallResultRow({ r }) {
  const o = OUTCOME_STYLE[r.outcome] || OUTCOME_STYLE.pending;
  const Icon = o.Icon;
  return (
    <div className="flex items-center justify-between gap-2 text-xs bg-white rounded-md border border-slate-200 px-2.5 py-1.5" data-testid="broadcast-call-result">
      <div className="min-w-0">
        <span className="font-medium text-slate-800">{r.name || 'Unknown'}</span>
        <span className="text-slate-400 ml-2">{formatPhone(r.phone)}</span>
        {r.reason && ['failed', 'busy', 'no-answer', 'canceled'].includes(r.outcome)
          ? <div className="text-[10px] text-rose-500 truncate">{r.reason}</div> : null}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {r.duration ? <span className="text-[10px] text-slate-400">{r.duration}s</span> : null}
        <Badge variant="outline" className={`text-[10px] inline-flex items-center gap-1 ${o.cls}`}>
          <Icon size={11} className={r.outcome === 'pending' ? 'animate-spin' : ''} /> {o.label}
        </Badge>
      </div>
    </div>
  );
}

const DELIVERY_STYLE = {
  sent: { label: 'Delivered', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { label: 'Failed', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  skipped_paused: { label: 'Skipped (opted out)', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

function StatPill({ label, value, cls }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${cls}`}>
      <div className="text-lg font-semibold leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wide mt-1 opacity-80">{label}</div>
    </div>
  );
}

function DeliveryRow({ d }) {
  const s = DELIVERY_STYLE[d.status] || { label: d.status, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  return (
    <div className="flex items-center justify-between gap-2 text-xs bg-white rounded-md border border-slate-200 px-2.5 py-1.5" data-testid="broadcast-delivery-row">
      <div className="min-w-0">
        <span className="font-medium text-slate-800">{d.name || 'Unknown'}</span>
        <span className="text-slate-400 ml-2 truncate">{d.channel === 'sms' ? formatPhone(d.phone) : d.email}</span>
        {d.error || d.reason ? <div className="text-[10px] text-rose-500 truncate">{d.reason || d.error}</div> : null}
      </div>
      <Badge variant="outline" className={`text-[10px] shrink-0 ${s.cls}`}>{s.label}</Badge>
    </div>
  );
}

/** Detailed per-channel delivery report for one broadcast. */
function BroadcastReport({ det, channels, broadcastId, onRetried }) {
  const [retrying, setRetrying] = useState(false);
  const stats = det.stats || {};
  const deliveries = det.deliveries || [];
  const emailRows = deliveries.filter((d) => d.channel === 'email');
  const smsRows = deliveries.filter((d) => d.channel === 'sms');
  const calls = det.call_results || [];
  const oc = det.call_outcomes || {};
  const has = (c) => (channels || []).includes(c);
  const failedCount = (stats.email_failed || 0) + (stats.sms_failed || 0)
    + (oc.failed || 0) + (oc.busy || 0) + (oc['no-answer'] || 0);

  const doRetry = async () => {
    setRetrying(true);
    try {
      const r = await broadcastAPI.retryFailed(broadcastId);
      if (!r.data.eligible) {
        toast.info('No failed recipients to retry.');
      } else {
        toast.success(`Retrying ${r.data.eligible} failed recipient(s)…`);
        setTimeout(() => onRetried && onRetried(), 2500);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally { setRetrying(false); }
  };

  return (
    <div className="space-y-4" data-testid="broadcast-report">
      {failedCount > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
          <span className="text-xs text-rose-700 font-medium">{failedCount} recipient(s) failed or went unanswered.</span>
          <Button size="sm" variant="outline" onClick={doRetry} disabled={retrying}
                  className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-100" data-testid="broadcast-retry-btn">
            {retrying ? <Loader2 size={12} className="animate-spin mr-1" /> : <RotateCcw size={12} className="mr-1" />} Retry failed
          </Button>
        </div>
      )}
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatPill label="Recipients" value={stats.total ?? 0} cls="bg-slate-50 text-slate-700 border-slate-200" />
        {has('email') && <StatPill label="Emails sent" value={stats.email_sent ?? 0} cls="bg-sky-50 text-sky-700 border-sky-200" />}
        {has('email') && (stats.email_failed ? <StatPill label="Emails failed" value={stats.email_failed} cls="bg-rose-50 text-rose-700 border-rose-200" /> : null)}
        {has('sms') && <StatPill label="SMS sent" value={stats.sms_sent ?? 0} cls="bg-violet-50 text-violet-700 border-violet-200" />}
        {has('sms') && (stats.sms_failed ? <StatPill label="SMS failed" value={stats.sms_failed} cls="bg-rose-50 text-rose-700 border-rose-200" /> : null)}
        {has('voicemail') && <StatPill label="Calls placed" value={stats.calls_placed ?? 0} cls="bg-emerald-50 text-emerald-700 border-emerald-200" />}
      </div>

      {/* Voicemail outcome breakdown */}
      {has('voicemail') && Object.keys(oc).length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="broadcast-call-outcomes">
          {Object.entries(oc).map(([k, v]) => {
            const o = OUTCOME_STYLE[k] || OUTCOME_STYLE.pending;
            return <Badge key={k} variant="outline" className={`text-[10px] ${o.cls}`}>{o.label}: {v}</Badge>;
          })}
        </div>
      )}

      {/* Email deliveries */}
      {has('email') && (
        <ReportSection title={`Email · ${emailRows.length}`} rows={emailRows} render={(d, i) => <DeliveryRow key={i} d={d} />} empty="No email recipients." />
      )}
      {/* SMS deliveries */}
      {has('sms') && (
        <ReportSection title={`SMS · ${smsRows.length}`} rows={smsRows} render={(d, i) => <DeliveryRow key={i} d={d} />} empty="No SMS recipients." />
      )}
      {/* Calls */}
      {has('voicemail') && (
        <ReportSection title={`Voicemail · ${calls.length}`} rows={calls} render={(r, i) => <CallResultRow key={i} r={r} />} empty="No call data." />
      )}
    </div>
  );
}

function ReportSection({ title, rows, render, empty }) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between text-[11px] font-semibold text-slate-600 mb-1.5">
        <span>{title}</span>
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>
      {open && (
        rows.length === 0
          ? <p className="text-[11px] text-slate-400">{empty}</p>
          : <div className="space-y-1 max-h-72 overflow-y-auto pr-1">{rows.map(render)}</div>
      )}
    </div>
  );
}


